import { Auth, API } from 'aws-amplify';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import awsconfig from '../aws-exports';

/**
 * Returns true if the app is currently running against the staging environment.
 *
 * Prod and staging share a single Cognito user pool, so Cognito deletion must
 * be skipped in staging to avoid permanently removing real users.
 *
 * Detection is based on the REST API endpoint suffix in aws-exports.js, which
 * Amplify sets to "/staging" for staging builds and "/prod" for production.
 */
export const isStaging = () => {
  const endpoints = awsconfig.aws_cloud_logic_custom || [];
  return endpoints.some((ep) => ep.endpoint?.includes('/staging'));
};

/**
 * Looks up the DynamoDB user record for the given Cognito sub (UUID).
 * Returns null if no active record is found.
 */
const fetchDbUserByCognitoId = async (cognitoID) => {
  const response = await API.graphql({
    query: queries.usersByCognitoID,
    variables: { cognitoID },
  });
  const items = response.data?.usersByCognitoID?.items || [];
  return items.find((u) => !u._deleted) || null;
};

/**
 * Calls the GraphQL deleteUser mutation, which marks the DynamoDB record as
 * _deleted: true (Amplify DataStore soft delete) without touching Cognito.
 */
const softDeleteDbUser = async (dbUser) => {
  if (!dbUser?.id) return;
  await API.graphql({
    query: mutations.deleteUser,
    variables: {
      input: {
        id: dbUser.id,
        _version: dbUser._version,
      },
    },
  });
};

/**
 * Deletes the current authenticated user account.
 *
 * Behaviour differs by environment:
 *
 *   Staging  → Only soft-deletes the DynamoDB record (_deleted: true).
 *              The Cognito account is intentionally preserved because prod
 *              and staging share the same user pool.
 *
 *   Production → Deletes the user from Cognito (Auth.deleteUser) AND
 *                soft-deletes the DynamoDB record.
 *
 * @param {Object|null} dbUser
 *   Pre-fetched DynamoDB user record ({ id, _version, ... }).
 *   When null the function fetches it automatically from the current
 *   authenticated Cognito session.
 *
 * @returns {Promise<{ staging: boolean }>}
 *   Resolves with { staging: true } when in staging so the caller can
 *   show an appropriate message if needed.
 */
export const deleteCurrentUserAccount = async (dbUser = null) => {
  const staging = isStaging();

  // Resolve the current Cognito user regardless of environment so we can
  // look up the DB record if it was not provided.
  const cognitoUser = await Auth.currentAuthenticatedUser();
  const cognitoID = cognitoUser.attributes.sub;

  const resolvedDbUser = dbUser || (await fetchDbUserByCognitoId(cognitoID));

  if (staging) {
    // Staging: soft-delete in DynamoDB only — do NOT remove from Cognito.
    await softDeleteDbUser(resolvedDbUser);
    console.info(
      '[deleteAccount] Staging env detected — Cognito user preserved, DB record soft-deleted.'
    );
  } else {
    // Production: hard-delete from Cognito, then clean up the DB record.
    await Auth.deleteUser();
    await softDeleteDbUser(resolvedDbUser);
  }

  return { staging };
};
